import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PropertyCalculatorState } from './types';
import {
    T_getParameters,
    T_getLibraryParameters,
    T_getParameterById,
    T_createParameter,
    T_updateParameter,
    T_deleteParameter,
    T_copyParameter,
    T_getAvailableAssetTypes,
    T_getTagAttributesForCalculator
} from './thunk';
import { Parameter } from 'api/propertyCalculator/types';

const initialState: PropertyCalculatorState = {
    myParameters: [],
    libraryParameters: [],
    selectedParameter: null,
    availableAssetTypes: [],
    tagAttributes: [],
    loading: false,
    error: null,
    currentAssetType: 'Subsurface Assets',
    searchTerm: ''
};

export const propertyCalculatorSlice = createSlice({
    name: 'propertyCalculator',
    initialState,
    reducers: {
        setSelectedParameter: (state, action: PayloadAction<Parameter | null>) => {
            state.selectedParameter = action.payload;
        },
        setCurrentAssetType: (state, action: PayloadAction<string>) => {
            state.currentAssetType = action.payload;
        },
        setSearchTerm: (state, action: PayloadAction<string>) => {
            state.searchTerm = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        // Get Parameters
        builder.addCase(T_getParameters.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_getParameters.fulfilled, (state, action) => {
            state.loading = false;
            state.myParameters = action.payload.myParameters;
            state.libraryParameters = action.payload.libraryParameters;
        });
        builder.addCase(T_getParameters.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Get Library Parameters
        builder.addCase(T_getLibraryParameters.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_getLibraryParameters.fulfilled, (state, action) => {
            state.loading = false;
            state.libraryParameters = action.payload;
        });
        builder.addCase(T_getLibraryParameters.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Get Parameter By ID
        builder.addCase(T_getParameterById.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_getParameterById.fulfilled, (state, action) => {
            state.loading = false;
            state.selectedParameter = action.payload;
        });
        builder.addCase(T_getParameterById.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Create Parameter
        builder.addCase(T_createParameter.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_createParameter.fulfilled, (state, action) => {
            state.loading = false;
            state.myParameters.push(action.payload);
            state.selectedParameter = action.payload;
        });
        builder.addCase(T_createParameter.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Update Parameter
        builder.addCase(T_updateParameter.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_updateParameter.fulfilled, (state, action) => {
            state.loading = false;
            const index = state.myParameters.findIndex(p => p.id === action.payload.id);
            if (index !== -1) {
                state.myParameters[index] = action.payload;
            }
            if (state.selectedParameter?.id === action.payload.id) {
                state.selectedParameter = action.payload;
            }
        });
        builder.addCase(T_updateParameter.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Delete Parameter
        builder.addCase(T_deleteParameter.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_deleteParameter.fulfilled, (state, action) => {
            state.loading = false;
            state.myParameters = state.myParameters.filter(p => p.id !== action.payload);
            if (state.selectedParameter?.id === action.payload) {
                state.selectedParameter = null;
            }
        });
        builder.addCase(T_deleteParameter.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Copy Parameter
        builder.addCase(T_copyParameter.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_copyParameter.fulfilled, (state, action) => {
            state.loading = false;
            state.myParameters.push(action.payload);
            state.selectedParameter = action.payload;
        });
        builder.addCase(T_copyParameter.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Get Available Asset Types
        builder.addCase(T_getAvailableAssetTypes.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_getAvailableAssetTypes.fulfilled, (state, action) => {
            state.loading = false;
            state.availableAssetTypes = action.payload;
        });
        builder.addCase(T_getAvailableAssetTypes.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        // Get Tag Attributes For Calculator
        builder.addCase(T_getTagAttributesForCalculator.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(T_getTagAttributesForCalculator.fulfilled, (state, action) => {
            state.loading = false;
            state.tagAttributes = action.payload;
        });
        builder.addCase(T_getTagAttributesForCalculator.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
    }
});

export const { 
    setSelectedParameter, 
    setCurrentAssetType, 
    setSearchTerm,
    clearError 
} = propertyCalculatorSlice.actions;

export default propertyCalculatorSlice.reducer;

